import { useEffect, useCallback, useLayoutEffect } from 'react';
import { useCurrentBoardBackground } from './useBoardBackground';
import {
  makeHSL,
  hexToHSL,
  HSLToHex,
  N10,
  getHSLNumbersFromHSLString,
  getHSLFunctionArgs,
  darkenHSL,
  lightenHSL,
} from '@trello/colors';
import { hasValidContrastRatio } from '@trello/a11y';
import { sendErrorEvent } from '@trello/error-reporting';
import { dangerouslyGetComputedToken, useGlobalTheme } from '@trello/theme';
import {
  BOARD_THEME_DARK,
  BOARD_THEME_LIGHT,
  DynamicTokenMap,
} from './BoardTheme.types';
import { getDynamicTextColor } from './getDynamicTextColor';
import { getDynamicButtonBackgroundColors } from './getDynamicButtonBackgroundColors';
import { getDynamicNavItemBackgroundColors } from './getDynamicNavItemBackgroundColors';
import { defaultTheme, useBoardThemeVariables } from './useBoardThemeVariables';

export const BACKGROUND_BRIGHTNESS_DARK = 'dark';
export const BACKGROUND_BRIGHTNESS_LIGHT = 'light';
export const BACKGROUND_BRIGHTNESS_UNKNOWN = 'unknown';
const BACKGROUND_TRANSPARENCY = 0.9;
export const OPACITY_DARK_1 = 'hsla(0,0%,0%,0.16)';

export function useBoardTheme(): null {
  const [
    boardThemeVariables,
    setBoardThemeVariables,
  ] = useBoardThemeVariables();

  const {
    backgroundBrightness,
    backgroundTopColor,
    backgroundColor,
    isBackgroundImage,
  } = useCurrentBoardBackground();

  const getBackgroundWithValidContrast = useCallback(
    (dynamicTextColor, backgroundTopColor) => {
      // convert to HSL
      // eslint-disable-next-line @trello/enforce-variable-case
      let backgroundTopColorHSL = hexToHSL(backgroundTopColor);

      // only check contrast for image backgrounds (not solid / gradient bgs)
      if (!isBackgroundImage) {
        return backgroundTopColorHSL;
      }

      const CONTRAST_RATIO_LEVEL_AA = 4.5;
      const CONTRAST_RATIO_LEVEL_AAA = 7;
      const CONTRAST_RATIO_LEVEL_BETWEEN_AA_AND_AAA =
        (CONTRAST_RATIO_LEVEL_AA + CONTRAST_RATIO_LEVEL_AAA) / 2;
      // Wrap `hasValidContrastRatio` to ensure we're using consistent args.
      const validateContrastRatio = (backgroundColor: string) =>
        hasValidContrastRatio(
          backgroundColor,
          dynamicTextColor,
          14,
          false,
          CONTRAST_RATIO_LEVEL_BETWEEN_AA_AND_AAA,
        );

      const { isValid, contrastRatio } = validateContrastRatio(
        backgroundTopColor,
      );

      if (isValid || !contrastRatio) {
        return backgroundTopColorHSL;
      }

      let hasValidContrast = false;
      let count = 0;
      // for best performance, only do this 10 times or until lightness is at 0% or 100%
      while (!hasValidContrast && count < 10) {
        count++;

        // get the parts of HSL so can adjust lightness (L)
        const [h, s, l] = getHSLNumbersFromHSLString(backgroundTopColorHSL);

        let newLightness = l;
        if (backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK) {
          // decrease lightness value by 20%
          newLightness *= 0.8;
          // lightness below 0 is meaningless, so set to 0% and break out of the loop
          if (newLightness <= 0) {
            return makeHSL(h, s, 0);
          }
        }

        if (backgroundBrightness === BACKGROUND_BRIGHTNESS_LIGHT) {
          // increase lightness value by 20%
          newLightness *= 1.2;
          // lightness above 1 is meaningless, so set to 100% and break out of the loop
          if (newLightness >= 1) {
            return makeHSL(h, s, 1);
          }
        }

        backgroundTopColorHSL = makeHSL(h, s, newLightness);

        const hexToCheck = HSLToHex(h, s, newLightness);

        const { isValid, contrastRatio } = validateContrastRatio(hexToCheck);
        if (isValid && contrastRatio) {
          // valid contrast found, exit loop
          hasValidContrast = true;
        }
      }

      return backgroundTopColorHSL;
    },
    [backgroundBrightness, isBackgroundImage],
  );

  const distinguishBackgroundColors = useCallback(
    (backgroundTopColorHSL) => {
      // Distinguish global header background color (dynamicBackgroundColor) from
      // workspace navigation background color (dynamicBackgroundColorTransparent)
      // by adding 5% lightness if brightness is light or removing 5% lightness
      // if brightness is dark.
      if (backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK) {
        return darkenHSL(backgroundTopColorHSL, 0.05);
      }

      if (backgroundBrightness === BACKGROUND_BRIGHTNESS_LIGHT) {
        return lightenHSL(backgroundTopColorHSL, 0.05);
      }

      return backgroundTopColorHSL;
    },
    [backgroundBrightness],
  );

  const { theme } = useGlobalTheme();

  useEffect((): void => {
    try {
      const {
        dynamicIconColor,
        dynamicTextColor,
        dynamicTextColorTransparent,
      } = getDynamicTextColor(backgroundBrightness);

      // determine dynamic background color
      let dynamicBackgroundColor = dangerouslyGetComputedToken(
        'elevation.surface',
        N10,
      );
      let dynamicBackgroundColorTransparent: string | undefined = undefined;

      // only do these calculations for image backgrounds
      if (isBackgroundImage) {
        // ensure background color meets WCAG AA contrast ratio
        // eslint-disable-next-line @trello/enforce-variable-case
        const backgroundTopColorHSL = getBackgroundWithValidContrast(
          dynamicTextColor,
          backgroundTopColor,
        );
        const hueSaturationLightness = getHSLFunctionArgs(
          backgroundTopColorHSL,
        );
        // eslint-disable-next-line @trello/enforce-variable-case
        const adjustedBackgroundTopColorHSL = distinguishBackgroundColors(
          backgroundTopColorHSL,
        );
        dynamicBackgroundColorTransparent = `hsla(${hueSaturationLightness},${BACKGROUND_TRANSPARENCY})`;
        dynamicBackgroundColor = adjustedBackgroundTopColorHSL;
      }

      // Pass in a setter fn so that TypeScript doesn't treat this as a partial.
      setBoardThemeVariables(() => ({
        theme:
          backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK
            ? BOARD_THEME_DARK
            : BOARD_THEME_LIGHT,
        // for solid color or gradient backgrounds always use a dark opacity background
        // for image backgrounds set this dynamically
        dynamicBackgroundColor: backgroundColor
          ? OPACITY_DARK_1
          : dynamicBackgroundColor,
        dynamicBackgroundColorTransparent: backgroundColor
          ? OPACITY_DARK_1
          : dynamicBackgroundColorTransparent,
        dynamicIconColor,
        dynamicTextColor,
        dynamicTextColorTransparent,
        ...getDynamicButtonBackgroundColors(backgroundBrightness, !!theme),
        ...getDynamicNavItemBackgroundColors(backgroundBrightness, !!theme),
      }));
    } catch (e) {
      // if something fails while doing these computations, default to a light theme
      setBoardThemeVariables(defaultTheme);

      // @ts-expect-error
      sendErrorEvent(e, {
        tags: {
          ownershipArea: 'trello-teamplates',
          feature: 'BoardTheme',
        },
      });
    }
  }, [
    backgroundBrightness,
    backgroundTopColor,
    backgroundColor,
    isBackgroundImage,
    setBoardThemeVariables,
    getBackgroundWithValidContrast,
    distinguishBackgroundColors,
    theme,
  ]);

  // Listen for changes in boardThemeVariables and assign them to the root node.
  useLayoutEffect(() => {
    // attach global CSS Variables to the document so
    // that even React portalled content can access
    // (e.g. Nachos Popover, some Atlaskit components)
    const root: HTMLElement = document.documentElement;

    if (!root) {
      return;
    }

    const { theme, ...dynamicVariables } = boardThemeVariables;

    requestAnimationFrame(() => {
      ([BOARD_THEME_DARK, BOARD_THEME_LIGHT] as const).forEach((className) => {
        root.classList.toggle(className, theme === className);
      });

      Object.entries(dynamicVariables).forEach(([key, value]) => {
        const tokenKey = DynamicTokenMap[key as keyof typeof dynamicVariables];
        if (value) {
          root.style.setProperty(tokenKey, value);
        } else {
          root.style.removeProperty(tokenKey);
        }
      });
    });

    // Theme is set wholesale in this hook, so we don't need to worry about
    // targeting individual dependencies.
  }, [boardThemeVariables]);

  return null;
}
